<template>
  <div>
    <b-card
      title="Ajouter un manager"
      aria-label="Formulaire d'ajout d'un manager"
    >
      <b-form
        ref="managerForm"
        aria-label="Formulaire d'ajout d'un manager"
        @submit.prevent="saveManager"
      >
        <!-- Informations personnelles -->
        <h5 class="mb-2">
          <feather-icon
            icon="UserIcon"
            class="mr-1"
            aria-hidden="true"
          />
          Informations personnelles du manager
        </h5>

        <b-row>
          <b-col md="6">
            <b-form-group label-class="form-label-premium"
              label="Nom complet du manager *"
              label-for="username"
              description="Saisissez le nom complet tel qu'il figure sur les documents officiels. Champ obligatoire."
            >
              <validation-provider
                #default="{ errors, valid }"
                name="nom"
                rules="required"
                mode="eager"
              >
                <div class="position-relative">
                  <b-form-input
                    id="username"
                    v-model="form.username"
                    :state="errors.length > 0 ? false : (valid && form.username ? true : null)"
                    placeholder="Entrez le nom complet du manager"
                    aria-label="Nom complet du manager"
                    class="premium-input"
                  />
                  <feather-icon
                    v-if="valid && form.username"
                    icon="CheckCircleIcon"
                    size="18"
                    class="input-success-icon"
                  />
                </div>
                <small
                  v-if="errors[0]"
                  class="text-danger validation-message-animate"
                >{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>

          <b-col md="6">
            <b-form-group label-class="form-label-premium"
              label="Adresse e-mail professionnelle *"
              label-for="email"
              description="Saisissez l'adresse e-mail professionnelle du manager. Champ obligatoire."
            >
              <validation-provider
                #default="{ errors, valid }"
                name="email"
                rules="required|email|asyncEmail"
                mode="eager"
              >
                <b-input-group class="input-group-merge">
                  <b-form-input
                    id="email"
                    v-model="form.email"
                    type="email"
                    :state="errors.length > 0 ? false : (valid && form.email ? true : null)"
                    placeholder="Entrez l'adresse e-mail professionnelle"
                    aria-label="Adresse e-mail professionnelle"
                    class="premium-input"
                  />
                  <b-input-group-append is-text>
                    <feather-icon
                      icon="RefreshCwIcon"
                      :class="form.username && form.username.trim().length > 0 ? 'cursor-pointer text-primary' : 'text-muted cursor-not-allowed'"
                      v-b-tooltip.hover="emailGenerationTooltip"
                      @click="form.username && form.username.trim().length > 0 ? generateCompanyEmail() : null"
                    />
                  </b-input-group-append>
                </b-input-group>
                <small
                  v-if="errors[0]"
                  class="text-danger validation-message-animate"
                >{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>

          <b-col md="6">
            <b-form-group label-class="form-label-premium"
              label="téléphone professionnel *"
              label-for="phone"
              description="Saisissez le numéro de téléphone professionnel du manager (8 chiffres, uniquement des chiffres). Champ obligatoire."
            >
              <validation-provider
                #default="{ errors, valid }"
                name="téléphone"
                rules="required|digits:8|asyncPhone"
                mode="eager"
              >
                <div class="position-relative">
                  <b-form-input
                    id="phone"
                    v-model="form.phone"
                    placeholder="Entrez le numéro de téléphone professionnel"
                    type="tel"
                    maxlength="8"
                    :formatter="formatPhone"
                    :state="errors.length > 0 ? false : (valid && form.phone ? true : null)"
                    aria-label="Numéro de téléphone professionnel"
                    class="premium-input"
                    @keypress="allowOnlyNumbers"
                  />
                  <feather-icon
                    v-if="valid && form.phone"
                    icon="CheckCircleIcon"
                    size="18"
                    class="input-success-icon"
                  />
                </div>
                <small
                  v-if="errors[0]"
                  class="text-danger validation-message-animate"
                >{{ errors[0] }}</small>
                <b-form-text v-else>
                  Format: 8 chiffres, sans espaces
                </b-form-text>
              </validation-provider>
            </b-form-group>
          </b-col>

          <b-col md="6">
            <b-form-group label-class="form-label-premium"
              label="téléphone personnel"
              label-for="phone2"
            >
              <b-form-input class="premium-input"
                id="phone2"
                v-model="form.phone2"
                placeholder="Entrez le numéro de téléphone personnel (optionnel)"
                type="tel"
                maxlength="8"
                :formatter="formatPhone"
                @keypress="allowOnlyNumbers"
              />
              <b-form-text>Format: 8 chiffres, sans espaces</b-form-text>
            </b-form-group>
          </b-col>
        </b-row>

        <b-form-group label-class="form-label-premium"
          label="Adresse"
          label-for="address"
        >
          <b-form-textarea
            id="address"
            v-model="form.address"
            placeholder="Entrez l'adresse complète"
            rows="3"
          />
        </b-form-group>

        <b-form-group label-class="form-label-premium"
          label="CIN"
          label-for="cin"
        >
          <b-form-input class="premium-input"
            id="cin"
            v-model="form.cin"
            placeholder="Entrez le numéro de CIN (optionnel)"
            maxlength="8"
            :formatter="formatCIN"
            :state="cinState"
            @keypress="allowOnlyNumbers"
          />
          <b-form-invalid-feedback v-if="form.cin && !cinState">
            Le numéro de CIN doit contenir 8 chiffres
          </b-form-invalid-feedback>
          <b-form-text>Format: 8 chiffres, sans espaces</b-form-text>
        </b-form-group>

        <!-- Repository Assignment -->
        <h5 class="mb-2 mt-4">
          <feather-icon
            icon="HomeIcon"
            class="mr-1"
          />
          Affectation au dépôt
        </h5>

        <validation-provider
          #default="{ errors, valid }"
          name="dépôt"
          rules="required"
          mode="eager"
        >
          <b-form-group label-class="form-label-premium"
            label="Dépôt *"
            label-for="repository"
          >
            <div class="position-relative">
              <v-select
                id="repository"
                v-model="form.repository"
                :options="repositories"
                label="name"
                :reduce="repo => repo.id"
                placeholder="Sélectionnez un dépôt"
                :class="[
                  'enhanced-select premium-select',
                  errors.length > 0 ? 'is-invalid' : (valid && form.repository ? 'is-valid' : '')
                ]"
                :clearable="true"
                :searchable="true"
                :loading="repositoriesLoading"
                :disabled="repositoriesLoading"
                @input="onRepositoryChange"
              >
                <template #no-options>
                  <span v-if="repositoriesLoading">Chargement des dépôts...</span>
                  <span v-else>Aucun dépôt disponible</span>
                </template>
                <template #selected-option="{ name }">
                  <div class="d-flex align-items-center">
                    <feather-icon
                      icon="HomeIcon"
                      size="16"
                      class="mr-2"
                      style="color: #114092;"
                    />
                    <span>{{ name }}</span>
                  </div>
                </template>
                <template #option="{ name }">
                  <div class="d-flex align-items-center">
                    <feather-icon
                      icon="HomeIcon"
                      size="16"
                      class="mr-2"
                      style="color: #114092;"
                    />
                    <span>{{ name }}</span>
                  </div>
                </template>
                <template #spinner="{ loading }">
                  <div
                    v-if="loading"
                    class="vs__spinner"
                  >
                    <div class="loading-dots">
                      <div class="dot1" />
                      <div class="dot2" />
                      <div class="dot3" />
                    </div>
                  </div>
                </template>
              </v-select>
              <feather-icon
                v-if="valid && form.repository"
                icon="CheckCircleIcon"
                size="18"
                class="input-success-icon select-icon"
              />
            </div>
            <small
              v-if="errors[0]"
              class="text-danger validation-message-animate"
            >{{ errors[0] }}</small>
          </b-form-group>
        </validation-provider>

        <!-- Access Information -->
        <h5 class="mb-2 mt-4">
          <feather-icon
            icon="LockIcon"
            class="mr-1"
          />
          Informations de connexion
        </h5>

        <b-row>
          <b-col md="6">
            <b-form-group label-class="form-label-premium"
              label="Mot de passe *"
              label-for="password"
            >
              <validation-provider
                #default="{ errors, valid }"
                name="mot de passe"
                rules="required|min:8"
                mode="eager"
              >
                <div class="position-relative">
                  <b-input-group class="input-group-merge">
                    <b-form-input
                      id="password"
                      v-model="form.password"
                      :type="showPassword ? 'text' : 'password'"
                      :state="errors.length > 0 ? false : (valid && form.password ? true : null)"
                      placeholder="Entrez un mot de passe"
                      class="premium-input"
                    />
                    <b-input-group-append
                      is-text
                      @click="showPassword = !showPassword"
                    >
                      <feather-icon
                        :icon="showPassword ? 'EyeOffIcon' : 'EyeIcon'"
                        class="cursor-pointer"
                      />
                    </b-input-group-append>
                  </b-input-group>
                  <feather-icon
                    v-if="valid && form.password"
                    icon="CheckCircleIcon"
                    size="18"
                    class="input-success-icon password-icon"
                    style="right: 3.5rem;"
                  />
                </div>
                <small
                  v-if="errors[0]"
                  class="text-danger validation-message-animate"
                >{{ errors[0] }}</small>
                <b-form-text v-else>
                  Le mot de passe doit comporter au moins 8 caractères
                </b-form-text>
              </validation-provider>
            </b-form-group>
          </b-col>

          <b-col md="6">
            <b-form-group label-class="form-label-premium"
              label="Confirmer le mot de passe *"
              label-for="confirm-password"
            >
              <validation-provider
                #default="{ errors, valid }"
                name="confirmation"
                rules="required|confirmed:mot de passe"
                mode="eager"
              >
                <div class="position-relative">
                  <b-input-group class="input-group-merge">
                    <b-form-input
                      id="confirm-password"
                      v-model="form.confirm_password"
                      :type="showConfirmPassword ? 'text' : 'password'"
                      :state="errors.length > 0 ? false : (valid && form.confirm_password ? true : null)"
                      placeholder="Confirmez le mot de passe"
                      class="premium-input"
                    />
                    <b-input-group-append
                      is-text
                      @click="showConfirmPassword = !showConfirmPassword"
                    >
                      <feather-icon
                        :icon="showConfirmPassword ? 'EyeOffIcon' : 'EyeIcon'"
                        class="cursor-pointer"
                      />
                    </b-input-group-append>
                  </b-input-group>
                  <feather-icon
                    v-if="valid && form.confirm_password"
                    icon="CheckCircleIcon"
                    size="18"
                    class="input-success-icon password-icon"
                    style="right: 3.5rem;"
                  />
                </div>
                <small
                  v-if="errors[0]"
                  class="text-danger validation-message-animate"
                >{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Generate Password Button -->
        <div class="text-center mb-3 mt-2">
          <PasswordGenerator
            :form-data="form"
            user-type="manager"
            @generated="onPasswordGenerated"
          />
        </div>

        <!-- Submit buttons -->
        <div class="d-flex justify-content-between mt-4">
          <b-button
            variant="outline-secondary"
            :to="{ name: 'manager-list' }"
          >
            <feather-icon
              icon="XIcon"
              size="16"
              class="mr-1"
            />
            Annuler
          </b-button>
          <span id="manager-create-save-btn-wrapper" class="d-inline-block">
            <b-button
              variant="primary"
              type="submit"
              :disabled="!isFormValid || isSubmitting"
              :class="{ 'not-allowed': !isFormValid || isSubmitting }"
            >
              <b-spinner
                v-if="isSubmitting"
                small
                class="mr-1"
              />
              <feather-icon
                v-else
                icon="SaveIcon"
                size="16"
                class="mr-1"
              />
              Enregistrer
            </b-button>
          </span>
          <b-tooltip
            v-if="!isFormValid || isSubmitting"
            target="manager-create-save-btn-wrapper"
            triggers="hover"
            placement="top"
          >
            {{ tooltipMessage }}
          </b-tooltip>
        </div>
      </b-form>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BButton,
  BRow,
  BCol,
  BSpinner,
  BFormInvalidFeedback,
  BInputGroup,
  BInputGroupAppend,
  BFormText,
  BTooltip,
} from 'bootstrap-vue'
import { ValidationProvider } from 'vee-validate'
import vSelect from 'vue-select'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import PasswordGenerator from '@core/components/password/PasswordGenerator.vue'

export default {
  name: 'ManagerCreate',

  components: {
    PasswordGenerator,
    BCard,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BButton,
    BRow,
    BCol,
    BSpinner,
    BFormInvalidFeedback,
    BInputGroup,
    BInputGroupAppend,
    BFormText,
    BTooltip,
    ValidationProvider,
    vSelect,
  },

  data() {
    return {
      isSubmitting: false,
      submitted: false,
      repositories: [],
      repositoriesLoading: true,
      form: {
        username: '',
        email: '',
        phone: '',
        phone2: '',
        address: '',
        cin: '',
        password: '',
        confirm_password: '',
        repository: null,
      },
      showPassword: false,
      showConfirmPassword: false,
    }
  },

  computed: {
    companyDomain() {
      return process.env.VUE_APP_COMPANY_EMAIL_DOMAIN || null
    },

    emailGenerationTooltip() {
      if (!this.companyDomain) {
        return 'Erreur: Variable d\'environnement VUE_APP_COMPANY_EMAIL_DOMAIN non définie'
      }
      if (this.form.username && this.form.username.trim().length > 0) {
        return `Générer un email par défaut à partir du nom (@${this.companyDomain}). Modifiable si nécessaire.`
      }
      return 'Veuillez d\'abord saisir le nom du manager'
    },

    nameState() {
      return this.form.username ? true : null
    },

    emailState() {
      if (!this.form.email) return null
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return re.test(this.form.email)
    },

    passwordState() {
      if (!this.form.password) return null
      return this.form.password.length >= 8
    },

    confirmPasswordState() {
      if (!this.form.confirm_password) return null
      return this.form.confirm_password === this.form.password
    },

    phoneState() {
      if (!this.form.phone) return null
      return this.form.phone.length === 8 && /^\d+$/.test(this.form.phone)
    },

    repositoryState() {
      return this.form.repository ? true : null
    },

    cinState() {
      if (!this.form.cin) return true // Optional field
      return this.form.cin.length === 8 && /^\d+$/.test(this.form.cin)
    },

    isFormValid() {
      return (
        this.form.username !== ''
        && this.form.email !== ''
        && this.emailState
        && this.form.phone !== ''
        && this.phoneState
        && this.form.repository !== null
        && this.form.password !== ''
        && this.form.password.length >= 8
        && this.form.confirm_password !== ''
        && this.form.password === this.form.confirm_password
        && this.cinState
      )
    },

    tooltipMessage() {
      if (this.isSubmitting) return 'Enregistrement en cours...'

      const missing = []
      if (!this.form.username || !this.form.username.trim()) missing.push('Nom')
      if (!this.form.email || !this.form.email.trim() || !this.emailState) missing.push('Email')
      if (!this.form.phone || !this.form.phone.trim() || !this.phoneState) missing.push('Téléphone')
      if (!this.form.repository) missing.push('Dépôt')
      if (!this.form.password || this.form.password.length < 8) missing.push('Mot de passe')
      if (!this.form.confirm_password || this.form.password !== this.form.confirm_password) missing.push('Confirmation mot de passe')
      if (this.form.cin && !this.cinState) missing.push('CIN valide')

      if (missing.length === 0) return ''
      if (missing.length === 1) {
        return `Veuillez renseigner le champ : ${missing[0]}.`
      }
      if (missing.length <= 3) {
        return `Veuillez remplir les champs obligatoires : ${missing.join(', ')}.`
      }
      return `Veuillez remplir les champs obligatoires manquants (${missing.length} champs).`
    },
  },

  created() {
    this.fetchRepositories()
  },

  mounted() {
    // Ensure proper CSS loading after component mount
    this.$nextTick(() => {
      this.refreshSelectStyles()
    })
  },

  // Handle route changes for proper CSS reloading
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.$nextTick(() => {
        vm.refreshSelectStyles()
      })
    })
  },

  activated() {
    // For keep-alive components
    this.$nextTick(() => {
      this.refreshSelectStyles()
    })
  },

  methods: {
    stripAccents(value) {
      if (!value) return ''
      return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    },

    onPasswordGenerated(generatedPassword) {
      this.form.password = generatedPassword
      this.form.confirm_password = generatedPassword
      this.showPassword = true
      this.showConfirmPassword = true
    },

    generateCompanyEmail() {
      if (!this.form.username || !this.form.username.trim()) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Attention',
            icon: 'AlertCircleIcon',
            text: 'Veuillez d\'abord saisir le nom du manager.',
            variant: 'warning',
          },
        })
        return
      }

      const username = this.form.username.trim()
      const cleanUsername = this.stripAccents(username)
        .toLowerCase()
        .replace(/\s+/g, '.')
        .replace(/[^a-z0-9.]/g, '')

      const generatedEmail = `${cleanUsername}@${this.companyDomain}`
      this.form.email = generatedEmail

      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Email généré',
          icon: 'CheckCircleIcon',
          text: `Email: ${generatedEmail}`,
          variant: 'success',
        },
      })
    },

    refreshSelectStyles() {
      // Force refresh of v-select styles
      const selectElements = this.$el.querySelectorAll('.enhanced-select')
      selectElements.forEach(element => {
        // Trigger a reflow to ensure styles are applied
        const originalDisplay = element.style.display
        // eslint-disable-next-line no-param-reassign
        element.style.display = 'none'
        // eslint-disable-next-line no-unused-expressions
        element.offsetHeight // trigger reflow
        // eslint-disable-next-line no-param-reassign
        element.style.display = originalDisplay || ''
      })
    },
    allowOnlyNumbers(event) {
      const { charCode, target } = event
      if (charCode < 48 || charCode > 57) {
        event.preventDefault()
      }

      // Also prevent input if already at 8 digits
      if (target.id === 'phone' && this.form.phone && this.form.phone.length >= 8) {
        event.preventDefault()
      }

      if (target.id === 'phone2' && this.form.phone2 && this.form.phone2.length >= 8) {
        event.preventDefault()
      }

      if (target.id === 'cin' && this.form.cin && this.form.cin.length >= 8) {
        event.preventDefault()
      }
    },

    formatPhone(value) {
      return value.replace(/\D/g, '').substring(0, 8)
    },

    formatCIN(value) {
      return value.replace(/\D/g, '').substring(0, 8)
    },

    async fetchRepositories() {
      this.repositoriesLoading = true
      try {
        const { data } = await axios.get('/api/repositories/')
        this.repositories = data

        // Ensure styles are applied after data loads
        this.$nextTick(() => {
          this.refreshSelectStyles()
        })
      } catch (error) {
        this.showToast({
          variant: 'danger',
          title: 'Erreur',
          text: 'Impossible de charger les dépôts',
        })
        // eslint-disable-next-line no-console
        console.error('Error fetching repositories:', error)
      } finally {
        this.repositoriesLoading = false
      }
    },

    onRepositoryChange(value) {
      // Handle repository selection change
      this.form.repository = value
      // Clear submitted state for this field if it's now valid
      if (value && this.submitted) {
        this.submitted = false
        this.$nextTick(() => {
          this.submitted = true
        })
      }
    },

    validateForm() {
      this.submitted = true

      // Basic validation
      if (!this.form.username) {
        this.showToast({
          variant: 'warning',
          title: 'Validation échouée',
          text: 'Le nom complet est requis',
        })
        return false
      }

      if (!this.form.email || !this.emailState) {
        this.showToast({
          variant: 'warning',
          title: 'Validation échouée',
          text: 'Une adresse email valide est requise',
        })
        return false
      }

      if (!this.form.password || !this.passwordState) {
        this.showToast({
          variant: 'warning',
          title: 'Validation échouée',
          text: 'Le mot de passe doit comporter au moins 8 caractères',
        })
        return false
      }

      if (this.form.password !== this.form.confirm_password) {
        this.showToast({
          variant: 'warning',
          title: 'Validation échouée',
          text: 'Les mots de passe ne correspondent pas',
        })
        return false
      }

      if (!this.form.repository) {
        this.showToast({
          variant: 'warning',
          title: 'Validation échouée',
          text: 'Veuillez sélectionner un dépôt',
        })
        return false
      }

      if (!this.form.phone || !this.phoneState) {
        this.showToast({
          variant: 'warning',
          title: 'Validation échouée',
          text: 'Le numéro de téléphone doit contenir 8 chiffres',
        })
        return false
      }

      if (this.form.cin && !this.cinState) {
        this.showToast({
          variant: 'warning',
          title: 'Validation échouée',
          text: 'Le numéro de CIN doit contenir 8 chiffres',
        })
        return false
      }

      return true
    },

    async saveManager() {
      // Prevent double submission
      if (this.isSubmitting) return

      // Validate the form
      if (!this.validateForm()) return

      this.isSubmitting = true

      try {
        // Prepare payload
        const payload = {
          ...this.form,
          // Ensure we send valid data types
          repository: this.form.repository,
        }

        // Make API request to create manager
        await axios.post('/api/auth/register/manager/', payload)

        // Show success message
        this.showToast({
          variant: 'success',
          title: 'Succès',
          text: 'Manager créé avec succès',
        })

        // Redirect to managers list
        setTimeout(() => {
          this.$router.push({ name: 'manager-list' })
        }, 1000)
      } catch (error) {
        // Handle errors
        // eslint-disable-next-line no-console
        console.error('Error creating manager:', error)

        let errorMessage = 'Erreur lors de la création du manager'

        if (error.response && error.response.data) {
          const errorData = error.response.data

          if (typeof errorData === 'object' && errorData !== null) {
            const messages = []
            // Handle both { field: [error] } and { errors: { field: [error] } }
            const fieldsToProcess = (errorData.errors && typeof errorData.errors === 'object')
              ? errorData.errors
              : errorData

            Object.keys(fieldsToProcess).forEach(field => {
              const fieldError = fieldsToProcess[field]
              const rawError = Array.isArray(fieldError) ? fieldError[0] : fieldError
              if (typeof rawError === 'string') {
                if (rawError.includes('existe') || rawError.includes('already exists')) {
                  if (field === 'phone') messages.push('Ce numéro de téléphone est déjà utilisé.')
                  else if (field === 'email') messages.push('Cette adresse email est déjà utilisée.')
                  else if (field === 'username') messages.push('Ce nom est déjà utilisé.')
                  else messages.push(`Le champ ${field} est déjà utilisé.`)
                } else {
                  messages.push(rawError)
                }
              }
            })

            if (messages.length > 0) {
              errorMessage = messages.join(' ')
            } else if (errorData.message) {
              errorMessage = errorData.message
            } else if (errorData.detail) {
              errorMessage = errorData.detail
            }
          } else if (typeof errorData === 'string') {
            errorMessage = errorData
          }
        }

        this.showToast({
          variant: 'danger',
          title: 'Erreur',
          text: errorMessage,
        })
      } finally {
        this.isSubmitting = false
      }
    },

    showToast({ variant, title, text }) {
      let icon

      if (variant === 'success') {
        icon = 'CheckCircleIcon'
      } else if (variant === 'warning') {
        icon = 'AlertTriangleIcon'
      } else {
        icon = 'AlertCircleIcon'
      }

      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon,
          text,
          variant,
        },
      }, {
        position: 'top-right',
        timeout: 5000,
      })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.form-group {
  margin-bottom: 1.5rem;
}

.form-control:focus,
.custom-select:focus {
  border-color: #114092;
  box-shadow: 0 3px 10px 0 rgba(17, 64, 146, 0.1);
}

/* Enhanced select dropdown styling - global version for better reliability */
.enhanced-select {
  .vs__dropdown-toggle {
    padding: 6px 12px !important;
    border: 1px solid #d8d6de !important;
    border-radius: 0.357rem !important;
    background-color: #fff !important;
    transition: all 0.3s ease-in-out !important;
    min-height: 42px !important;

    &:hover {
      border-color: #b8b6c0 !important;
    }
  }

  &.vs--open .vs__dropdown-toggle {
    border-color: #114092 !important;
    box-shadow: 0 3px 10px 0 rgba(17, 64, 146, 0.1) !important;
    background-color: #fff !important;
  }

  &.vs--focused .vs__dropdown-toggle {
    border-color: #114092 !important;
    box-shadow: 0 3px 10px 0 rgba(17, 64, 146, 0.1) !important;
  }

  .vs__selected {
    margin: 2px 2px !important;
    padding: 4px 8px !important;
    color: #5e5873 !important;
    background-color: rgba(17, 64, 146, 0.1) !important;
    border-radius: 4px !important;
    font-weight: 500 !important;
    display: flex !important;
    align-items: center !important;
  }

  .vs__selected-options {
    padding: 2px !important;
    flex-wrap: wrap !important;
  }

  .vs__actions {
    padding-right: 8px !important;
  }  .vs__clear {
    color: #b9b9c3 !important;

    &:hover {
      color: #114092 !important;
    }
  }

  .vs__open-indicator {
    color: #b9b9c3 !important;
    transition: transform 0.3s ease !important;

    &:hover {
      color: #114092 !important;
    }
  }

  &.vs--open .vs__open-indicator {
    transform: rotate(180deg) !important;
  }

  .vs__search {
    padding: 4px 0 !important;
    margin: 0 !important;

    &::placeholder {
      color: #b9b9c3 !important;
    }

    &:focus {
      outline: none !important;
    }
  }

  .vs__dropdown-menu {
    padding: 0 !important;
    border: 1px solid #d8d6de !important;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1) !important;
    border-radius: 0.357rem !important;
    background: #fff !important;
    z-index: 1050 !important;
    max-height: 200px !important;
    overflow-y: auto !important;
  }

  .vs__dropdown-option {
    padding: 12px 20px !important;
    color: #5e5873 !important;
    transition: all 0.2s ease !important;
    cursor: pointer !important;
    border-bottom: 1px solid #f0f0f0 !important;

    &:last-child {
      border-bottom: none !important;
    }

    &:hover, &--highlight {
      background: linear-gradient(135deg, #f8f8f8 0%, #f0f0f0 100%) !important;
      color: #114092 !important;
    }

    &--selected {
      background: linear-gradient(135deg, #114092 0%, #0d3475 100%) !important;
      color: #fff !important;
      font-weight: 600 !important;
    }

    &--disabled {
      background: #f5f5f5 !important;
      color: #b9b9c3 !important;
      cursor: not-allowed !important;
    }
  }

  .vs__no-options {
    padding: 20px !important;
    text-align: center !important;
    color: #b9b9c3 !important;
    font-style: italic !important;
  }

  .vs__spinner {
    border-color: #114092 transparent transparent !important;
  }

  /* Focus state enhancement */
  &:focus-within .vs__dropdown-toggle {
    border-color: #114092 !important;
    box-shadow: 0 3px 10px 0 rgba(17, 64, 146, 0.1) !important;
  }

  /* Error state styling */
  &.is-invalid .vs__dropdown-toggle {
    border-color: #ea5455 !important;

    &:focus, &:hover {
      border-color: #ea5455 !important;
      box-shadow: 0 3px 10px 0 rgba(234, 84, 85, 0.1) !important;
    }
  }

  /* Success state styling */
  &.is-valid .vs__dropdown-toggle {
    border-color: #28c76f !important;

    &:focus, &:hover {
      border-color: #28c76f !important;
      box-shadow: 0 3px 10px 0 rgba(40, 199, 111, 0.1) !important;
    }
  }
}

/* Ensure proper z-index for dropdowns */
.vs__dropdown-menu {
  z-index: 1050 !important;
}

/* Form enhancements */
.card {
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1) !important;
  border: none !important;
  border-radius: 0.75rem !important;
}

.card-title {
  font-weight: 700 !important;
  color: #5e5873 !important;
}

h5 {
  color: #5e5873 !important;
  font-weight: 600 !important;
  margin-bottom: 1rem !important;

  .feather {
    color: #114092 !important;
  }
}

/* Form labels styling */
.form-group label {
  font-weight: 600 !important;
  color: #5e5873 !important;
  margin-bottom: 0.5rem !important;
}

/* Form controls enhanced styling */
.form-control {
  border: 1px solid #d8d6de !important;
  border-radius: 0.357rem !important;
  padding: 0.75rem 1rem !important;
  transition: all 0.3s ease !important;

  &:focus {
    border-color: #114092 !important;
    box-shadow: 0 3px 10px 0 rgba(17, 64, 146, 0.1) !important;
  }

  &:hover {
    border-color: #b8b6c0 !important;
  }
}

/* Button styling */
.btn-primary {
  background: linear-gradient(135deg, #114092 0%, #0d3475 100%) !important;
  border: none !important;
  box-shadow: 0 4px 15px rgba(17, 64, 146, 0.3) !important;
  transition: all 0.3s ease !important;

  &:hover {
    transform: translateY(-2px) !important;
    box-shadow: 0 6px 20px rgba(17, 64, 146, 0.4) !important;
  }
}

.btn-outline-secondary {
  border-color: #d8d6de !important;
  color: #5e5873 !important;

  &:hover {
    background-color: #f8f8f8 !important;
    border-color: #b8b6c0 !important;
    color: #5e5873 !important;
  }
}

/* Force CSS re-application after navigation */
.page-enter-active, .page-leave-active {
  transition: opacity 0.3s;
}

.page-enter, .page-leave-to {
  opacity: 0;
}

/* Ensure v-select is always properly styled */
.enhanced-select * {
  box-sizing: border-box !important;
}

/* Fix for navigation CSS loading issues */
.router-view {
  transition: all 0.3s ease;
}

/* Loading state for repository select */
.enhanced-select.vs--loading .vs__open-indicator {
  animation: spin 1s linear infinite !important;
}

.enhanced-select .vs__spinner {
  .loading-dots {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px;

    div {
      width: 4px;
      height: 4px;
      margin: 0 2px;
      background-color: #114092;
      border-radius: 50%;
      animation: loading-bounce 1.4s ease-in-out infinite both;
    }

    .dot1 { animation-delay: -0.32s; }
    .dot2 { animation-delay: -0.16s; }
    .dot3 { animation-delay: 0s; }
  }
}

@keyframes loading-bounce {
  0%, 80%, 100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Enhancement for better visual feedback */
.form-group.has-error .enhanced-select .vs__dropdown-toggle {
  border-color: #ea5455 !important;
  animation: shake 0.5s ease-in-out !important;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  75% { transform: translateX(5px); }
}

/* Ensure proper z-index stacking */
.enhanced-select .vs__dropdown-menu {
  z-index: 9999 !important;
  position: absolute !important;
}

/* Fix for modal/overlay issues */
.modal .enhanced-select .vs__dropdown-menu {
  z-index: 1060 !important;
}

/* Premium Input Styling with Real-time Validation */
.premium-input {
  transition: all 0.3s ease;
  padding: 0.75rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;

  &:focus {
    border-color: #114092;
    box-shadow: 0 0 0 3px rgba(17, 64, 146, 0.15);
  }

  &.is-valid {
    border-color: #28c76f !important;
    background-color: #f0fdf4;
    padding-right: 3rem;

    &:focus {
      border-color: #28c76f !important;
      box-shadow: 0 0 0 3px rgba(40, 199, 111, 0.15);
    }
  }

  &.is-invalid {
    border-color: #ea5455 !important;
    background-color: #fef2f2;

    &:focus {
      border-color: #ea5455 !important;
      box-shadow: 0 0 0 3px rgba(234, 84, 85, 0.15);
    }
  }
}

.input-success-icon {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #28c76f;
  pointer-events: none;
  z-index: 5;
  animation: checkFadeIn 0.3s ease;

  &.textarea-icon {
    top: 1rem;
    transform: none;
  }

  &.select-icon {
    right: 3rem;
  }

  &.password-icon {
    right: 3.5rem;
  }
}

@keyframes checkFadeIn {
  from {
    opacity: 0;
    transform: translateY(-50%) scale(0.5);
  }
  to {
    opacity: 1;
    transform: translateY(-50%) scale(1);
  }
}

.validation-message-animate {
  display: inline-flex;
  align-items: center;
  animation: fadeInDown 0.3s ease;
  margin-top: 0.25rem;
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* V-Select Real-time Validation */
.premium-select {
  &.is-valid .vs__dropdown-toggle {
    border: 2px solid #28c76f !important;
    background-color: #f0fdf4;
  }

  &.is-invalid .vs__dropdown-toggle {
    border: 2px solid #ea5455 !important;
    background-color: #fef2f2;
  }
}

/* Submit Button Cursor Feedback */
.btn.not-allowed,
.btn:disabled {
  cursor: not-allowed !important;
  opacity: 0.6 !important;
  pointer-events: auto !important;
}
</style>
